import type { LearnerExam } from "../content-schema";
import { fixedModules } from "./fixed-practice";

export type Timing = "untimed" | "timed";
export type TimingAction = "pause" | "resume" | "finish-module" | "start-next-module" | "skip-break";
export type ClockState = {
  phase: string;
  deadline: number | null;
  pausedRemainingMs: number | null;
  currentModuleId: string | null;
  currentPosition: number;
  selectedRoutes?: Record<string,string>;
};
export function supportsTiming(exam: LearnerExam) {
  const modules = fixedModules(exam);
  return exam.assessmentModel === "digital_sat" && (exam.eligibility === "fixed_ready" || exam.eligibility === "adaptive_ready") && modules.length === 4 &&
    modules.every((module) => module.complete && module.items.length === module.expectedCount && module.items.every((item) => item.questionId));
}
export const moduleDuration = (section: string) => (section === "math" ? 35 : 32) * 60_000;

function finish(state: ClockState, exam: LearnerExam, at: number): ClockState {
  const modules = fixedModules(exam);
  const index = modules.findIndex((module) => module.id === state.currentModuleId);
  if (index < 0) throw new Error("Attempt module is unavailable");
  const next = modules[index + 1];
  const phase = !next ? "completed" : modules[index].section !== next.section ? "break" : "ready";
  return { ...state, phase, deadline: phase === "break" ? at + 600_000 : null, pausedRemainingMs: null };
}
export function reconcileClock(state: ClockState, exam: LearnerExam, now: number): ClockState {
  if (state.deadline === null || state.deadline > now) return state;
  let next = state.phase === "running" ? finish(state, exam, state.deadline) : state;
  if (next.phase === "break" && next.deadline !== null && next.deadline <= now) next = { ...next, phase: "ready", deadline: null };
  return next;
}
export function transitionClock(state: ClockState, exam: LearnerExam, action: TimingAction, now: number): ClockState {
  if (action === "pause" && (state.phase === "running" || state.phase === "break") && state.deadline !== null && state.deadline > now)
    return { ...state, phase: state.phase === "running" ? "paused_module" : "paused_break", pausedRemainingMs: state.deadline - now, deadline: null };
  if (action === "pause" && state.phase === "ready") return state;
  if (action === "resume" && (state.phase === "paused_module" || state.phase === "paused_break") && state.pausedRemainingMs !== null)
    return { ...state, phase: state.phase === "paused_module" ? "running" : "break", deadline: now + state.pausedRemainingMs, pausedRemainingMs: null };
  if (action === "finish-module" && state.phase === "running" && state.deadline !== null && state.deadline > now) return finish(state, exam, now);
  if (action === "skip-break" && state.phase === "break") return { ...state, phase: "ready", deadline: null };
  if (action === "start-next-module" && state.phase === "ready") {
    const modules = fixedModules(exam);
    const next = modules[modules.findIndex((module) => module.id === state.currentModuleId) + 1];
    if (next) return { phase: "running", deadline: now + moduleDuration(next.section), pausedRemainingMs: null, currentModuleId: next.id, currentPosition: next.items[0].position };
  }
  throw new Error("This timing action is unavailable. Reload the attempt to see its current state.");
}
